import React, { useState } from "react";
import styled from "styled-components";
import { Repeater, types } from "react-bricks/frontend";
import { AccordionItemProps } from "./AccordionItem";
import { color } from "styles/colours";

enum Background {
  white = "white",
  gray = "gray",
}

enum Spacing {
  one = "1rem",
  two = "2rem",
  three = "3rem",
}

interface AccordionProps {
  spacing?: Spacing;
  background?: Background;
  items: types.RepeaterItems<AccordionItemProps>;
}

export const Accordion: types.Brick<AccordionProps> = ({
  spacing = Spacing.one,
  items,
  background = Background.white,
}) => {
  const { backgroundColor } = getPropsFromBackground(background);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <Wrapper>
      <StyledAccordion spacing={spacing} backgroundColor={backgroundColor}>
        <Repeater
          propName="accordions"
          items={items}
          itemProps={{
            activeIndex,
            setActiveIndex,
          }}
        />
      </StyledAccordion>
    </Wrapper>
  );
};

const getPropsFromBackground = (c: Background) => {
  switch (c) {
    case Background.white:
      return {
        backgroundColor: color.white,
      };
    case Background.gray:
      return {
        backgroundColor: color.B210,
      };
  }
};

Accordion.schema = {
  name: "accordion",
  label: "Accordion",
  category: "Accordion Components",
  previewImageUrl: "/static/bricks/accordian.png",
  // Define repeaters for child bricks
  repeaterItems: [
    {
      name: "accordions",
      itemType: "accordion-item",
      itemLabel: "Accordion",
      min: 1,
      max: 10,
    },
  ],
  // Define default props
  getDefaultProps: () => ({
    background: Background.white,
    space: Spacing.one,
    accordions: [
      {
        title: "Accordion Title",
        content: "Accordion content goes here.",
      },
      {
        title: "Accordion Title 2",
        content: "Accordion content goes here.",
      },
    ],
  }),
  // Define side edit props
  sideEditProps: [
    {
      name: "background",
      label: "Background Color",
      type: types.SideEditPropType.Select,
      selectOptions: {
        display: types.OptionsDisplay.Select,
        options: Object.values(Background).map((value) => ({ value, label: value })),
      },
    },
    {
      name: "spacing",
      label: "Item Spacing",
      type: types.SideEditPropType.Select,
      selectOptions: {
        display: types.OptionsDisplay.Radio,
        options: Object.values(Spacing).map((value) => ({ value, label: value })),
      },
    },
  ],
};

const Wrapper = styled.div`
  margin: auto;
  max-width: 80rem;
`;

export const StyledAccordion = styled.section<{
  spacing: string;
  backgroundColor: string;
}>`
  background: ${(p) => p.backgroundColor || color.white};
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.spacing};
`;
